import { createFileRoute } from "@tanstack/react-router";
import DOMPurify from "dompurify";
import { Newspaper } from "lucide-react";
import { useEffect, useRef } from "react";
import { usePublicConfig } from "@/api/queries/config";
import { useNews } from "@/api/queries/news";
import { EmptyState } from "@/components/shared/EmptyState";
import { Card } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import { useAuth } from "@/hooks/use-auth";
import { usePageMeta } from "@/hooks/use-page-meta";
import type { User } from "@/types/user";

export const Route = createFileRoute("/_app/news/")({
	component: NewsPage,
});

// Allow data-content attribute through DOMPurify
DOMPurify.addHook("uponSanitizeAttribute", (_node, data) => {
	if (data.attrName === "data-content") {
		data.forceKeepAttr = true;
	}
});

/**
 * Replaces the text content of elements with data-content="user-*" attributes.
 * Mirrors the old frontend behavior from news.pug:
 *   e.dataset.content.split('-')[1] -> api.user.info()[value]
 */
function replaceUserDataContent(container: HTMLElement, user: User) {
	const elements = container.querySelectorAll<HTMLElement>('[data-content^="user-"]');
	const userRecord = user as unknown as Record<string, unknown>;

	for (const el of elements) {
		const attrValue = el.dataset.content;
		if (!attrValue) continue;

		const key = attrValue.split("-")[1];
		if (!key) continue;

		const value = userRecord[key];
		if (value != null) {
			// Old frontend prepends a space: ' ' + value
			el.textContent = ` ${String(value)}`;
		}
	}
}

function NewsPage() {
	const { data: newsItems, isLoading } = useNews();
	const { user } = useAuth();
	const { data: config } = usePublicConfig();
	const newsContainerRef = useRef<HTMLDivElement>(null);

	usePageMeta({
		title: config ? `News - ${config.seo.defaultTitle}` : undefined,
	});

	// Post-processing: replace data-content="user-*" elements after render
	useEffect(() => {
		if (!newsContainerRef.current || !user || !newsItems?.length) return;
		replaceUserDataContent(newsContainerRef.current, user);
	}, [newsItems, user]);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-20">
				<Spinner size="lg" label="News werden geladen" />
			</div>
		);
	}

	if (!newsItems || newsItems.length === 0) {
		return (
			<EmptyState
				icon={<Newspaper size={48} />}
				title="Keine News vorhanden"
				description="Es gibt aktuell keine Neuigkeiten."
			/>
		);
	}

	return (
		<div ref={newsContainerRef} className="max-w-3xl mx-auto space-y-6">
			<h1 className="text-2xl font-bold text-text">News</h1>
			{newsItems.map((item) => {
				// Platzhalter ersetzen
				let content = item.content;
				if (user?.name) {
					content = content.replaceAll("%customer_username%", user.name);
				}

				// HTML sanitizen (data-content wird durch den Hook oben beibehalten)
				const sanitizedContent = DOMPurify.sanitize(content);

				return (
					<Card key={`${item.headline}-${item.created_at}`} padding="md">
						<h2 className="text-lg font-semibold text-text mb-2">{item.headline}</h2>
						<time className="text-xs text-text-muted block mb-3">
							{new Date(item.created_at).toLocaleDateString("de-DE", {
								day: "2-digit",
								month: "2-digit",
								year: "numeric",
							})}
						</time>
						<div
							className="text-text-secondary prose prose-sm max-w-none"
							dangerouslySetInnerHTML={{ __html: sanitizedContent }}
						/>
					</Card>
				);
			})}
		</div>
	);
}
